import { Button, Group, HoverCard, Stack, Text } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import useRequestCredits from "../hooks/useRequestCredits";
import { notifications } from "@mantine/notifications"; // Import notifications
import { useEffect, useState } from "react";
import useScreenWidth from "@/utils/hooks/useScreenWidth";
import { IconFolderBolt, IconSparkles } from "@tabler/icons-react";
import { RETURN_LABEL } from "../constants";
import { AppRoutes } from "@/constants.ts";
import { useAuthStore } from "@utils/stores/authStore.ts";

interface NavCreditsProps {
  credits: number;
  hasRequestedCredits?: boolean;
}

const NavCredits: React.FC<NavCreditsProps> = ({
  credits,
  hasRequestedCredits,
}: NavCreditsProps) => {
  const [hasRequested, setHasRequested] = useState(
    hasRequestedCredits || false
  );
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 992;

  const navigate = useNavigate();
  const { isOwner } = useAuthStore();

  const {
    isRequestingCredits,
    mantineAlert,
    handleRequestCredits,
    hasPendingRequest,
  } = useRequestCredits();

  useEffect(() => {
    setHasRequested(hasRequestedCredits || hasPendingRequest || false);
  }, [hasRequestedCredits, hasPendingRequest]);

  const handleRequest: () => Promise<void> = async () => {
    await handleRequestCredits();
  };

  // Show notification when mantineAlert changes
  useEffect(() => {
    if (mantineAlert) {
      notifications.show({
        title: mantineAlert.message === "success" ? "Success" : "Error",
        message:
          mantineAlert.message === "success"
            ? "Credits requested successfully!"
            : mantineAlert.message,
        color: mantineAlert.color,
        withCloseButton: true,
      });
    }
  }, [mantineAlert]);

  const renderButton = (): JSX.Element => {
    if (isOwner()) {
      return (
        <Group mt="md">
          <Button
            color="#0077CC"
            variant="light"
            radius="md"
            fw={700}
            fullWidth
            onClick={() => navigate(AppRoutes.returnSettings)}
            className="buy-credits-button"
          >
            View History
          </Button>
        </Group>
      );
    } else {
      return (
        <Group mt="md">
          <Button
            color="#0077CC"
            radius="md"
            fullWidth
            onClick={handleRequest} // Use handleRequestCredits from the hook
            loading={isRequestingCredits} // Show loading state while requesting credits
            disabled={hasRequested || hasPendingRequest} // Disable the button if credits have already been requested
          >
            {hasRequestedCredits || hasPendingRequest
              ? `Pending Request`
              : `Request Credits`}
          </Button>
        </Group>
      );
    }
  };

  return (
    <HoverCard
      width={300}
      shadow="md"
      radius="md"
      withArrow
      openDelay={200}
      closeDelay={400}
    >
      <div className="nav-credits-button">
        <HoverCard.Target>
          {isMobile ? (
            <Button
              variant="subtle"
              data-cy="navCredits"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <IconFolderBolt size={18} />
            </Button>
          ) : (
            <Button
              variant="light"
              color="#0077CC"
              size="sm"
              leftSection={<IconFolderBolt size={20} />}
              data-cy="navCredits"
              className="nav-credits-tooltip-button"
            >
              <Text fw="bold" c={"#0077CC"}>
                {credits}{" "}
                {credits > 1 || credits === 0
                  ? `${RETURN_LABEL}s`
                  : `${RETURN_LABEL}`}
              </Text>
            </Button>
          )}
        </HoverCard.Target>
      </div>
      <HoverCard.Dropdown>
        <Stack gap={0} p={8}>
          <div className="nav-credits-overview">
            <Group gap={4} align="center" justify="center">
              <IconSparkles size={20} color="#3BABFD" />
              <Text
                variant="gradient"
                gradient={{ from: "#3BABFD", to: "#4F67F6" }}
                fz={20}
                fw={800}
                ta="center"
              >
                Unlock One-Click
              </Text>
            </Group>

            <Text c="dimmed" fz={13} p={4} ta="center">
              <strong>1 {RETURN_LABEL}</strong> is used{" "}
              <strong>per client</strong>, unlocking our One-Click Return data
              entry feature - allowing you to complete a tax return in your tax
              software in just minutes.
            </Text>
          </div>
          {renderButton()}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

export default NavCredits;
